import { Check } from "lucide-react";
import { LANES, LANE_DETAILS, LANE_FAQ } from "@/data/site";
import { CTA } from "@/components/CTA";
import { Reveal } from "@/components/Reveal";
import { PageHeader, Kicker } from "@/components/Section";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { windowState } from "@/lib/windows";
import { RecruitmentTimeline } from "@/components/recruitment/RecruitmentTimeline";

const List = ({ title, items, tone = "ink" }) => (
  <div className={`h-full border p-6 ${tone === "pink" ? "border-gold/60 bg-onyx shadow-[0_0_30px_rgba(201,162,39,0.15)]" : "border-silver/20 bg-charcoal/70"}`}>
    <h3 className={`font-head text-xl font-extrabold uppercase tracking-tight ${tone === "pink" ? "text-goldlight" : "text-cream"}`}>{title}</h3>
    <ul className="mt-4 space-y-3">
      {items.map((it) => (
        <li key={it} className="flex gap-3 font-body text-sm leading-relaxed text-silver/85">
          <Check size={18} className="mt-0.5 shrink-0 text-gold" /> <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function Cohort() {
  return (
    <div className="bg-onyx text-cream">
      <PageHeader
        kicker="The Cohort"
        titleLines={["Pick your", "lane. Build", "your crew."]}
        subtitle="Every cohort is multidisciplinary. You bring your craft; the Village gives you a crew and a real brief to build around."
        dark
      />

      <section className="grain-dark border-b border-gold/20 bg-onyx py-20 sm:py-28">
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal><Kicker>Creative Lanes</Kicker></Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {LANES.map((lane, i) => (
              <Reveal key={lane.slug} delay={i * 0.05}>
                <div className="group overflow-hidden gold-frame bg-charcoal transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_35px_rgba(201,162,39,0.18)]">
                  <div className="panel-mono relative h-48 overflow-hidden">
                    <img src={lane.img} alt={lane.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-onyx/70 to-transparent" />
                    <span className="absolute left-3 top-3 border border-gold/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em] text-gold">Lane {String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="border-t border-gold/25 p-5">
                    <h3 className="font-display text-2xl uppercase leading-none tracking-tight text-cream">{lane.title}</h3>
                    <p className="mt-2 font-body text-sm leading-relaxed text-silver/75">{lane.blurb}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="grain-dark relative border-b border-gold/20 bg-charcoal py-20 sm:py-28">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal><Kicker>What to Expect</Kicker></Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            <Reveal delay={0}><List title="Eligibility" items={LANE_DETAILS.eligibility} /></Reveal>
            <Reveal delay={0.06}><List title="Time Commitment" items={LANE_DETAILS.commitment} /></Reveal>
            <Reveal delay={0.12}><List title="Portfolio Benefits" items={LANE_DETAILS.portfolio} /></Reveal>
            <Reveal delay={0.18}><List title="Compensation" items={LANE_DETAILS.comp} tone="pink" /></Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="mt-6 flex items-start gap-3 border border-gold/40 bg-onyx p-5">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
              <p className="font-body font-semibold text-goldlight">{LANE_DETAILS.credit}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <RecruitmentTimeline />

      <section className="grain-dark bg-onyx py-20 sm:py-28">
        <div className="relative mx-auto max-w-[900px] px-4 sm:px-6">
          <Reveal><Kicker>FAQ</Kicker>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-5xl">Good questions.</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <Accordion type="single" collapsible className="space-y-3" data-testid="cohort-faq">
              {LANE_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border border-silver/20 bg-charcoal/70 px-5 transition-colors data-[state=open]:border-gold/50">
                  <AccordionTrigger className="font-head text-left text-lg font-extrabold uppercase tracking-tight text-cream hover:no-underline hover:text-goldlight">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-base leading-relaxed text-silver/80">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 text-center">
            {windowState("student").status === "open" ? (
              <CTA to="/apply/student" variant="pink" data-testid="cohort-page-cta">Submit Your Dossier</CTA>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <span className="border border-gold/50 bg-charcoal px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-goldlight" data-testid="cohort-page-sealed">
                  Applications sealed · Reopening {windowState("student").opensLabel}
                </span>
                <CTA to="/apply" variant="ink" data-testid="cohort-page-cta">Review the Recruitment File</CTA>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
